import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, Tooltip,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  Save, Refresh, Visibility, VisibilityOff, CheckCircle, Error,
  Warning, Settings, Security, Link as LinkIcon, Send, Email as EmailIcon,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const SECTIONS = {
  mode: { label: "Modo Operacional", icon: "🎯", color: "#6B48FF",
    help: "Define como este HUB opera: conectado ao Central OtimizAPP ou independente com Apps próprios." },
  central: { label: "Conexao com HUB Central", icon: "🔗", color: "#25D366",
    help: "Conecte este HUB ao servidor central OtimizAPP Connect. O administrador central fornece a URL e o token de acesso.",
    visibleWhen: "centralized" },
  meta: { label: "Meta (WhatsApp / Facebook / Instagram)", icon: "📱", color: "#1877F2",
    help: "Configurar seu proprio App Meta para Embedded Signup (WhatsApp, Facebook, Instagram).",
    actionLabel: "Abrir Meta Developers",
    actionUrl: "https://developers.facebook.com/apps/",
    visibleWhen: "independent",
    steps: [
      "1. Acesse developers.facebook.com e crie/selecione seu App",
      "2. Copie o App ID e App Secret",
      "3. Em WhatsApp → Configuração, copie o Config ID",
      "4. Cole nos campos abaixo e salve",
    ] },
  telegram: { label: "Telegram", icon: "✈️", color: "#0088CC",
    help: "Registrar em: my.telegram.org/apps (1 por HUB, serve para todos os canais).",
    actionLabel: "Abrir my.telegram.org",
    actionUrl: "https://my.telegram.org/apps",
    visibleWhen: "independent",
    steps: [
      "1. Acesse my.telegram.org/apps com seu número",
      "2. Crie um novo App (qualquer nome/plataforma)",
      "3. Copie o App api_id e App api_hash",
      "4. Cole nos campos abaixo e salve",
    ] },
  marketplace: { label: "Marketplace", icon: "🛒", color: "#25D366",
    help: "Configurar seu proprio portal de assinaturas de canais.",
    visibleWhen: "independent" },
  security: { label: "Seguranca", icon: "🔒", color: "#6B48FF",
    help: "Chaves de criptografia, assinatura de webhooks e autenticacao." },
  services: { label: "Servicos", icon: "⚙️", color: "#FF6B00",
    help: "Redis, status dos servicos e reinicio." },
};

// Tradução dos valores de select para exibição
const OPTION_LABELS = {
  centralized: "Centralizado (OtimizAPP Connect)",
  independent: "Independente (Apps próprios)",
  full: "Completo (todos os canais)",
  meta_only: "Apenas Meta (WA, FB, IG)",
  disabled: "Desativado",
  permissive: "Permissivo (aceita, apenas loga)",
  strict: "Restrito (rejeita sem assinatura)",
};

const ALL_CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", ready: true },
  { id: "instagram", label: "Instagram", ready: true },
  { id: "facebook", label: "Facebook", ready: true },
  { id: "telegram", label: "Telegram", ready: true },
  { id: "email", label: "Email", ready: true },
  { id: "voip", label: "VoIP", ready: true },
  { id: "sip", label: "SIP Trunk", ready: true },
  { id: "tiktok", label: "TikTok", ready: false },
  { id: "sms", label: "SMS", ready: false },
  { id: "google_business", label: "Google Business", ready: false },
  { id: "mercadolivre", label: "Mercado Livre", ready: false },
  { id: "teams", label: "MS Teams", ready: false },
];

function SettingField({ field, onSave, onModeChange }) {
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [edited, setEdited] = useState(false);
  const [channelValues, setChannelValues] = useState(
    field.type === "channels" && field.valueMasked
      ? (typeof field.valueMasked === "string" ? field.valueMasked.split(",").filter(Boolean) : field.valueMasked)
      : ALL_CHANNELS.map((c) => c.id) // Default: todos habilitados
  );

  const handleSave = async () => {
    let value;
    if (field.type === "channels") {
      value = channelValues.join(",");
    } else {
      value = inputRef.current?.value;
    }
    if (!value && field.isSecret && field.configured) return;
    setSaving(true);
    try {
      await onSave(field.key, value);
      setEdited(false);
    } catch {}
    setSaving(false);
  };

  const toggleChannel = (chId) => {
    setChannelValues((prev) => {
      const next = prev.includes(chId) ? prev.filter((c) => c !== chId) : [...prev, chId];
      setEdited(true);
      return next;
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: "#D1D5DB", fontWeight: 500 }}>{field.label}</Typography>
        {field.isSecret && (
          <Chip size="small" label="criptografado" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(107,72,255,0.1)", color: "#6B48FF" }} />
        )}
        {field.requiresRestart && (
          <Chip size="small" label="requer reinicio" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(245,158,11,0.1)", color: "#F59E0B" }} />
        )}
        {!field.requiresRestart && !field.isSecret && field.type !== "channels" && (
          <Chip size="small" label="aplica na hora" sx={{ height: 14, fontSize: 8, bgcolor: "rgba(37,211,102,0.1)", color: "#25D366" }} />
        )}
      </Box>

      {field.description && (
        <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 0.5, lineHeight: 1.4 }}>{field.description}</Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {field.type === "channels" ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {ALL_CHANNELS.map((ch) => {
                const active = channelValues.includes(ch.id);
                const canToggle = ch.ready;
                return (
                  <Chip
                    key={ch.id}
                    size="small"
                    label={ch.ready ? ch.label : `${ch.label} (em breve)`}
                    onClick={canToggle ? () => toggleChannel(ch.id) : undefined}
                    sx={{
                      fontSize: 11, fontWeight: 500,
                      cursor: canToggle ? "pointer" : "default",
                      opacity: ch.ready ? 1 : 0.45,
                      bgcolor: !ch.ready ? "rgba(255,255,255,0.02)"
                        : active ? "rgba(37,211,102,0.15)" : "rgba(255,255,255,0.04)",
                      color: !ch.ready ? "#4B5563"
                        : active ? "#25D366" : "#6B7280",
                      border: `1px solid ${!ch.ready ? "rgba(255,255,255,0.05)"
                        : active ? "rgba(37,211,102,0.3)" : "rgba(255,255,255,0.08)"}`,
                    }}
                  />
                );
              })}
            </Box>
          ) : field.type === "select" ? (
            <select
              ref={inputRef}
              defaultValue={field.valueMasked || field.options?.[0] || ""}
              onChange={(e) => { setEdited(true); if (onModeChange) onModeChange(field.key, e.target.value); }}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#1a1d29",
                color: "#fff", fontSize: 13, outline: "none",
              }}
            >
              {(field.options || []).map((o) => <option key={o} value={o}>{OPTION_LABELS[o] || o}</option>)}
            </select>
          ) : (
            <input
              ref={inputRef}
              type={field.isSecret && !showSecret ? "password" : "text"}
              defaultValue={field.isSecret ? "" : (field.valueMasked || "")}
              placeholder={field.configured ? (field.isSecret ? "••••••••" : "") : "Nao configurado"}
              onChange={() => setEdited(true)}
              style={{
                width: "100%", padding: "8px 10px", borderRadius: 8,
                border: `1px solid ${edited ? "rgba(107,72,255,0.4)" : "rgba(255,255,255,0.1)"}`,
                backgroundColor: "#1a1d29", color: "#fff", fontSize: 13, outline: "none",
              }}
            />
          )}
        </Box>
        {field.isSecret && (
          <IconButton size="small" onClick={() => setShowSecret(!showSecret)} sx={{ color: "#6B7280" }}>
            {showSecret ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
          </IconButton>
        )}
        {field.type !== "channels" && (
          <Chip size="small" label={field.configured ? "OK" : "Pendente"} sx={{
            height: 20, fontSize: 9, fontWeight: 600, minWidth: 55,
            bgcolor: field.configured ? "rgba(37,211,102,0.1)" : "rgba(245,158,11,0.1)",
            color: field.configured ? "#25D366" : "#F59E0B",
          }} />
        )}
        {edited && (
          <Button size="small" onClick={handleSave} disabled={saving}
            sx={{ minWidth: 60, textTransform: "none", fontSize: 11, color: "#6B48FF", fontWeight: 600 }}>
            {saving ? <CircularProgress size={12} /> : "Salvar"}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function SettingsPage({ showSnackbar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  // Mode overrides — atualizam visibilidade em tempo real ao mudar selects
  const [modeOverrides, setModeOverrides] = useState({});

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data: resp } = await hubApi.getSettings();
      setData(resp);
    } catch {
      showSnackbar?.("Erro ao carregar configurações", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async (key, value) => {
    try {
      const { data: result } = await hubApi.updateSetting(key, value);
      showSnackbar?.(
        result.requiresRestart
          ? `${key} salvo. Reinicie o serviço para aplicar.`
          : `${key} salvo e aplicado!`
      );
      loadSettings();
    } catch (err) {
      showSnackbar?.("Erro ao salvar", "error");
      throw err;
    }
  };

  const handleTest = async (service) => {
    setTesting(service);
    try {
      const { data: result } = await hubApi.testService(service);
      setTestResults((prev) => ({ ...prev, [service]: result }));
      showSnackbar?.(result.status === "ok" ? `${service}: OK (${result.latencyMs}ms)` : `${service}: ${result.status}`);
    } catch {
      setTestResults((prev) => ({ ...prev, [service]: { status: "error" } }));
    }
    setTesting(null);
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6B48FF" }} /></Box>;
  }

  const settings = data?.settings || [];
  const services = data?.services || {};
  // HUB_ROLE vem do servidor (definido no .env, nunca editavel pelo painel).
  // central = servidor administrador OtimizAPP / local = HUB de parceiro/cliente
  const hubRole = (data?.hubRole || "local").toLowerCase();
  const isCentralRole = hubRole === "central";

  // Detectar modo atual (override local > valor salvo no backend)
  const getVal = (key) => modeOverrides[key] || (settings.find((s) => s.key === key)?.valueMasked) || "";
  const currentMode = getVal("HUB_MODE") || "centralized";
  const currentOwnership = getVal("META_APP_OWNERSHIP") || "central";
  const currentMarketplace = getVal("MARKETPLACE_MODE") || "full";
  const isIndependent = currentMode === "independent";

  // Resolver visibilidade por seção
  // HUB CENTRAL: gerencia tudo (Meta + Telegram + Marketplace + Segurança + Serviços) — sem opção de modo
  // HUB LOCAL Centralizado: só Conexão Central + Segurança + Serviços
  // HUB LOCAL Independente: Meta + Telegram + Marketplace + Segurança + Serviços
  // HUB Central: nao tem "modo" (ele E o central) → mostra Meta/Telegram/Marketplace direto
  // HUB Local Centralizado: mostra Conexao Central
  // HUB Local Independente: mostra Meta/Telegram/Marketplace proprios
  const sectionVisible = {
    mode: !isCentralRole,                                // Modo so faz sentido no Local
    central: !isCentralRole && !isIndependent,           // Conexao Central so no Local Centralizado
    meta: isCentralRole || isIndependent,                // Central (admin) ou Local Independente
    telegram: isCentralRole || isIndependent,
    marketplace: (isCentralRole || isIndependent) && currentMarketplace !== "disabled",
    security: true,
    services: true,
  };

  // Agrupar por seção (filtrar por visibilidade de seção + campo)
  const grouped = {};
  for (const s of settings) {
    if (!sectionVisible[s.section]) continue;
    // Filtro por campo: visibleWhen casa com o modo atual
    if (s.visibleWhen && s.visibleWhen !== currentMode) continue;
    if (!grouped[s.section]) grouped[s.section] = [];
    grouped[s.section].push(s);
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>Configuracoes</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Gerenciar integrações e serviços do HUB
          </Typography>
        </Box>
        <Tooltip title="Recarregar"><IconButton onClick={loadSettings} sx={{ color: "#6B7280" }}><Refresh /></IconButton></Tooltip>
      </Box>

      {/* Alertas */}
      {isCentralRole && (
        <Card sx={{ bgcolor: "rgba(107,72,255,0.06)", border: "1px solid rgba(107,72,255,0.2)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CheckCircle sx={{ fontSize: 18, color: "#6B48FF" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#6B48FF" }}>
                HUB Central — Administrador OtimizAPP
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Voce administra o ecossistema. Configure aqui os Apps Meta, Telegram e Marketplace
              que serao utilizados pelos HUBs Locais conectados em modo centralizado.
            </Typography>
          </CardContent>
        </Card>
      )}
      {!isCentralRole && currentMode === "independent" && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Warning sx={{ fontSize: 18, color: "#F59E0B" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#F59E0B" }}>
                Modo Independente
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Voce gerencia seus proprios Apps e configuracoes. Configure Meta, Telegram e Marketplace abaixo.
              Voce assume responsabilidade por permissoes, webhooks e manutencao.
            </Typography>
          </CardContent>
        </Card>
      )}
      {!isCentralRole && currentMode === "centralized" && (
        <Card sx={{ bgcolor: "rgba(37,211,102,0.06)", border: "1px solid rgba(37,211,102,0.15)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CheckCircle sx={{ fontSize: 18, color: "#25D366" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#25D366" }}>
                Modo Centralizado
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.5 }}>
              Conectado ao ecossistema OtimizAPP Connect. Meta, Telegram e Marketplace sao gerenciados pelo administrador central.
              Informe a URL e o token de acesso na secao "Conexao com HUB Central".
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {Object.entries(SECTIONS).map(([sectionKey, section]) => {
          // Filtrar seção por visibilidade
          if (!sectionVisible[sectionKey]) return null;
          const fields = grouped[sectionKey] || [];
          if (fields.length === 0 && sectionKey !== "services") return null;
          const allConfigured = fields.length > 0 && fields.every((f) => f.configured);
          const noneConfigured = fields.length > 0 && fields.every((f) => !f.configured);
          const testResult = testResults[sectionKey];

          return (
            <Grid item xs={12} md={6} key={sectionKey}>
              <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontSize: 22 }}>{section.icon}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#fff" }}>{section.label}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        label={allConfigured ? "Configurado" : noneConfigured ? "Pendente" : "Parcial"}
                        sx={{
                          fontSize: 10, height: 20,
                          bgcolor: allConfigured ? "rgba(37,211,102,0.1)" : noneConfigured ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                          color: allConfigured ? "#25D366" : noneConfigured ? "#EF4444" : "#F59E0B",
                        }}
                      />
                      {(sectionKey === "marketplace" || sectionKey === "services") && (
                        <Button
                          size="small"
                          onClick={() => handleTest(sectionKey === "services" ? "redis" : sectionKey)}
                          disabled={testing === sectionKey}
                          sx={{ minWidth: 50, textTransform: "none", fontSize: 10, color: "#6B48FF" }}
                        >
                          {testing === sectionKey ? <CircularProgress size={12} /> : "Testar"}
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {section.help && (
                    <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 1.5, lineHeight: 1.5 }}>
                      {section.help}
                    </Typography>
                  )}

                  {/* Passo a passo + botão de ação */}
                  {section.steps && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "rgba(107,72,255,0.04)", borderRadius: 2, border: "1px solid rgba(107,72,255,0.1)" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6B48FF", mb: 0.5 }}>Como configurar:</Typography>
                      {section.steps.map((step, i) => (
                        <Typography key={i} sx={{ fontSize: 13, color: "#D1D5DB", lineHeight: 1.8 }}>
                          {step}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {section.actionUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(section.actionUrl, "_blank")}
                      startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        mb: 2, textTransform: "none", fontSize: 11, fontWeight: 600,
                        color: section.color || "#6B48FF",
                        borderColor: (section.color || "#6B48FF") + "40",
                        "&:hover": { borderColor: section.color || "#6B48FF", bgcolor: (section.color || "#6B48FF") + "10" },
                      }}
                    >
                      {section.actionLabel || "Abrir"}
                    </Button>
                  )}

                  {/* Service status */}
                  {sectionKey === "services" && (
                    <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
                      <Chip size="small"
                        icon={services.redis === "connected" ? <CheckCircle sx={{ fontSize: 12, color: "#25D366 !important" }} /> : <Error sx={{ fontSize: 12, color: "#EF4444 !important" }} />}
                        label={`Redis: ${services.redis}`}
                        sx={{ fontSize: 10, height: 22, bgcolor: services.redis === "connected" ? "rgba(37,211,102,0.1)" : "rgba(239,68,68,0.1)", color: services.redis === "connected" ? "#25D366" : "#EF4444" }}
                      />
                      <Chip size="small"
                        icon={<CheckCircle sx={{ fontSize: 12, color: "#25D366 !important" }} />}
                        label="HUB: running"
                        sx={{ fontSize: 10, height: 22, bgcolor: "rgba(37,211,102,0.1)", color: "#25D366" }}
                      />
                    </Box>
                  )}

                  {/* Fields */}
                  {fields.map((field) => (
                    <SettingField
                      key={field.key}
                      field={field}
                      onSave={handleSave}
                      onModeChange={field.section === "mode" ? (k, v) => setModeOverrides((prev) => ({ ...prev, [k]: v })) : undefined}
                    />
                  ))}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
